import { ClassMatchType, MatchBracketType, ScheduleJobStatusEnum } from './enums';
import { Class, Court, Team, TournamentGroup, User } from './tournament';

export const itemsOnSameTimeSlot = (
    a: IMatchScheduleItem | IFreeSlotItem,
    b: IMatchScheduleItem | IFreeSlotItem
) => {
    return (
        (a.scheduleDate !== null || b.scheduleDate !== null) &&
        a.scheduleDate?.toString() === b.scheduleDate?.toString()
    );
};
export const isSameSlot = (a: IFreeSlotItem, b: IFreeSlotItem) => {
    return (
        a.day === b.day &&
        a.court.id === b.court.id &&
        a.scheduleDate.toString() === b.scheduleDate.toString()
    );
};

export const isFreeSlot = (item: any): item is IFreeSlotItem => !!item.isFreeSlot;
export type IFreeSlotItem = {
    isFreeSlot: boolean;
    day: number;
    court: Court;
    scheduleDate: string;
    duration?: number;
    /** ISO end of free slot (from API timeline) */
    scheduleEndDate?: string;
};

export const isScheduled = (item: any): item is IMatchScheduleItem | IFreeSlotItem =>
    !!item.scheduleDate;
export interface IMatchScheduleItem {
    id: number;
    name: string;
    classId: number;
    homeTeam: Team;
    awayTeam: Team;
    homePlayer1: User;
    homePlayer2: User | null;
    awayPlayer1: User;
    awayPlayer2: User | null;
    court: Court;
    type: ClassMatchType;
    status: number;
    isGold: boolean;
    tournamentGroup?: TournamentGroup;
    bracket: MatchBracketType;
    scheduleDate: string | null;
    startDate: string | null;
    /** Match completion time from API (not used for slot length when scheduleEndDate is set) */
    endDate: string | null;
    /** Planned end of scheduled slot (ISO string) */
    scheduleEndDate?: string | null;
    sortKey: number;
    homeTeamPlaceholder: string;
    awayTeamPlaceholder: string;
    matchId: string;
    isAdditional: boolean;
    duration?: number;
}

export interface IMatchScheduleItemDto {
    id: number;
    scheduleDate: Date | null;
    startDate: Date | null;
    scheduleEndDate: Date | null;
    court: Court | null;
    courtId: number | null;
}

export enum ScheduleMode {
    SEQUENTIAL,
}

export type ClassOption = {
    class: Class;
    courts: Court[];
    matchLengths: {
        [matchType in ClassMatchType]: number;
    };
};

export type IScheduleDayConstraints = {
    dayKey: number; // used to map form properly with Formik
    startTime: string;
    endTime: string;
    defaultMatchLength: number;
    defaultPauseLength: number;
    classes: Class[];
    classOptions: ClassOption[];
    courts: Court[];
};

export interface IScheduleConstraints extends IGlobalScheduleSettings {
    tournamentStartTime: string;
    tournamentEndTime: string;
    scheduleMode: ScheduleMode;
    days: IScheduleDayConstraints[];
    tournamentClasses: Class[];
    tournamentCourts: Court[];
}

export type IScheduleItem = IMatchScheduleItem | IFreeSlotItem;

export interface ICourtSchedule {
    court: Court;
    matches: IScheduleItem[];
}

export interface IDaySchedule {
    dayKey: number;
    courts: ICourtSchedule[];
}

export interface ITournamentSchedule {
    days: IDaySchedule[];
}

export interface ScheduleAndUnscheduledMatches {
    schedule: ITournamentSchedule;
    unscheduled: IMatchScheduleItem[];
}

export interface IScheduleJobStatusResponse {
  status: ScheduleJobStatusEnum;
  queuePosition: number;
  etaSeconds: number;
  errorMessage?: string;
}

export interface IGlobalScheduleSettings {
    allow_split: boolean;
    allow_alignment: boolean;
    pull_to_lower_court: boolean;
    gap_group_group: number;
    max_gap_group_group: number;
    gap_group_ko: number;
    max_gap_group_ko: number;
    gap_ko_ko: number;
    max_gap_ko_ko: number;
    max_span_limit?: number; 
    s1_time_limit: number;
    s2_time_limit: number;

    stream_courts: number[];
    max_chunk_size?: number;
    number_of_chunk_splits?: number;

    globalMatchLengths?: Record<string, number>;

    classOverrides?: Record<number, ICategoryOverride>;

    /** IANA timezone used when schedule wall-clock times were last authored. */
    scheduleTimezone?: string;
}

export interface ICategoryOverride {
    event_priority?: number; 
    span_limit?: number;     
    gap_group_group?: number;
    gap_group_ko?: number;
    gap_ko_ko?: number;

    matchLengths?: Record<string, number>;
}
